import { useState } from 'react';
import { PlateHelp } from './PlateHelp';

const CONFIG = {
  lb: { plates: [45, 35, 25, 10, 5, 2.5], bars: [45, 35, 15], ez: 15, def: 45 },
  kg: { plates: [25, 20, 15, 10, 5, 2.5, 1.25], bars: [20, 15, 10], ez: 10, def: 20 },
};
const SIZES = [
  { h: 44, w: 10, c: '#FFD700' }, { h: 40, w: 9, c: '#3b82f6' }, { h: 34, w: 8, c: '#22c55e' },
  { h: 26, w: 7, c: '#e5e5e5' }, { h: 20, w: 6, c: '#ef4444' }, { h: 16, w: 5, c: '#a3a3a3' }, { h: 13, w: 4, c: '#737373' },
];
const barKey = (unit) => `plate-calc-bar-${unit}`;

const BARBELL_RE = /barbell|bench press|squat|deadlift|overhead press|military press|good morning|power clean|snatch/i;
const NOT_BARBELL_RE = /dumbbell|\bdb\b|kettlebell|goblet|bodyweight|split squat|jump|air squat|machine|smith|cable|pistol|wall sit|sissy/i;

export const isBarbellExercise = (name = '') => BARBELL_RE.test(name) && !NOT_BARBELL_RE.test(name);

export const calcPlates = (total, bar, available = CONFIG.lb.plates) => {
  let left = (total - bar) / 2;
  const plates = [];
  if (left < 0) return { plates, loaded: bar, exact: total === bar, below: true };
  available.forEach((p) => {
    while (left >= p - 1e-9) { plates.push(p); left -= p; }
  });
  const loaded = bar + 2 * plates.reduce((a, b) => a + b, 0);
  return { plates, loaded: Number(loaded.toFixed(2)), exact: left < 0.01, below: false };
};

// e.g. [45, 25, 25, 5] -> "one 45 lb, two 25 lb and one 5 lb plate"
const WORDS = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
export const describePlates = (plates, unit) => {
  const counts = [];
  plates.forEach((p) => {
    const last = counts[counts.length - 1];
    if (last && last.p === p) last.n += 1; else counts.push({ p, n: 1 });
  });
  const parts = counts.map(({ p, n }) => `${WORDS[n] || n} ${p} ${unit}`);
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
  return `${list} plate${plates.length > 1 ? 's' : ''}`;
};

export const PlateCalculator = ({ weight, unit = 'lb' }) => {
  const cfg = CONFIG[unit];
  const [bars, setBars] = useState(() => ({
    lb: Number(localStorage.getItem(barKey('lb'))) || CONFIG.lb.def,
    kg: Number(localStorage.getItem(barKey('kg'))) || CONFIG.kg.def,
  }));
  const bar = cfg.bars.includes(bars[unit]) ? bars[unit] : cfg.def;
  const total = Number(weight) || 0;
  const pickBar = (b) => { setBars((prev) => ({ ...prev, [unit]: b })); localStorage.setItem(barKey(unit), String(b)); };
  const { plates, loaded, exact, below } = calcPlates(total, bar, cfg.plates);
  const style = (p) => SIZES[cfg.plates.indexOf(p)];
  const unitWord = unit === 'kg' ? 'kg' : 'lbs';
  const example = unit === 'kg'
    ? 'Example: 60 kg on the 20 kg bar → 40 kg of plates → 20 kg on each side.'
    : 'Example: 135 lb on the 45 lb bar → 90 lb of plates → 45 lb on each side.';

  return (
    <div className="mt-2 rounded-2xl bg-white/[0.03] border border-white/10 px-3 py-2.5" data-testid="plate-calculator">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Plates per side</span>
        <PlateHelp unit={unit} example={example} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <span className="text-[11px] text-white/50" data-testid="plate-bar-label">Your bar in {unitWord}:</span>
        <div className="flex gap-1" role="group" aria-label="Bar weight">
          {cfg.bars.map((b) => (
            <button
              type="button" key={b} onClick={() => pickBar(b)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${b === bar ? 'bg-primary text-black border-primary' : 'border-white/15 text-white/50 hover:text-white'}`}
              data-testid={`plate-bar-${b}`}
            >
              {b === cfg.ez ? `EZ ${b}` : b}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center h-12" aria-hidden="true">
        <div className="h-3 w-5 rounded-l bg-white/30" />
        <div className="h-6 w-1.5 bg-white/50" />
        <div className="flex items-center gap-[2px] ml-[2px]">
          {plates.map((p, i) => (
            <div key={i} className="rounded-[2px]" style={{ height: style(p).h, width: style(p).w, background: style(p).c }} />
          ))}
        </div>
        <div className="h-2 flex-1 bg-white/20 rounded-r ml-[2px]" />
      </div>
      <p className="text-xs text-white/80 mt-1" data-testid="plate-calc-text">
        {total <= 0
          ? 'Enter a weight to see which plates to load.'
          : below
            ? `That's lighter than the ${bar} ${unit} bar itself. Pick a lighter bar or add weight.`
            : plates.length
              ? <>
                  Load on each end: <b className="text-white">{describePlates(plates, unit)}</b>
                  <span className="block text-white/45 mt-0.5" data-testid="plate-calc-math">
                    {bar} {unit} bar + {Number(((loaded - bar) / 2).toFixed(2))} {unit} on each end = {loaded} {unit}
                  </span>
                </>
              : `Just the empty ${bar} ${unit} bar. No plates needed.`}
        {total > 0 && !below && !exact && (
          <span className="block text-yellow-300 mt-0.5" data-testid="plate-calc-closest">Can't load exactly {total} {unit} with standard plates. Closest: {loaded} {unit}</span>
        )}
      </p>
    </div>
  );
};

export default PlateCalculator;
